import React, { useEffect, useState } from "react"
import {
  Grid,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress
} from "@mui/material"
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from "@mui/icons-material"
import apiClient from "../../api/apiClient"
import { useDispatch } from "react-redux"
import { showModal } from "../../redux/modalSlice"
import { getErrorMessage } from "../../modules/helpers"

const CustomerFlagged = () => {
  // --- State Management ---
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(false)
  const dispatch=useDispatch()
  // Modal & Form State
  const [modalOpen, setModalOpen] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [editId, setEditId] = useState(null)
  const [deleteId, setDeleteId] = useState(null)

  const initialState = {
    name: "",
    email: "",
    address: ""
  }
  const [formData, setFormData] = useState(initialState)

  // --- Effects ---
  useEffect(() => {
    fetchCustomers()
  }, [])

  // --- API Functions ---
  const fetchCustomers = async () => {
    setLoading(true)

      const result = await apiClient.get("/customer-flagged?limit=1000000")
      // Assuming your API returns array in result.data or result.data.result
      // Adjust based on your specific backend response structure
      setLoading(false)
      setCustomers(result.data.results || []) 
      if (!result.ok) {
        console.error("Error fetching data:",   result?.data?.message)
      }

  }

  const handleDelete = async () => {
    const result= await apiClient.delete(`/customer-flagged/${deleteId}`)
    setDeleteId(null)
    if (!result.ok) {
      dispatch(
        showModal({
          error: true,
          title: "Client",
          message: getErrorMessage(result.data,"Failed to save data.")
        })
      )
      return
    }
    fetchCustomers() // Refresh table
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitLoading(true)
var result
      if (isEdit) {
        result=   await apiClient.put(`/customer-flagged/${editId}`, formData)
      } else {
        result= await apiClient.post("/customer-flagged", formData)
      }
      setSubmitLoading(false)
      setModalOpen(false)
      if (!result.ok) {
        dispatch(
          showModal({
            error: true,
            title: "Client",
            message: getErrorMessage(result.data,"Failed to save data.")
          })
        )
        return
      }
      fetchCustomers() // Refresh table

  }

  // --- Helper Functions ---
  const handleOpenModal = (customer = null) => {
    if (customer) {
      setIsEdit(true)
      setEditId(customer._id)
      setFormData({
        name: customer.name,
        email: customer.email,
        address: customer.address
      })
    } else {
      setIsEdit(false)
      setEditId(null)
      setFormData(initialState)
    }
    setModalOpen(true)
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setLoading(false)
  }

  const handleChange = (key, value) => {
    setFormData({ ...formData, [key]: value })
  }

  // --- Render ---
  return (
    <Box sx={{ px: { xs: 2, md: 3 } }}>
      {/* Page Header */}
      <Box sx={{ maxWidth: 1180, mx: "auto" }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
          <div className="d-flex justify-content-between align-items-center flex-column flex-sm-row mb-3">
            <Box>
              <h3 className="mb-1">Flagged Customers</h3>
              <p className="mb-0 text-muted">Manage customers that should be highlighted during sample intake.</p>
            </Box>
            <Button 
              variant="contained" 
              className="btn_bg_secondery text-white mt-2 mt-sm-0"
              startIcon={<AddIcon />}
              onClick={() => handleOpenModal()}
            >
              Add Customer
            </Button>
          </div>
          </Grid>
        </Grid>

      {/* Table Section */}
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #E5E7EB",
            borderRadius: 3,
            boxShadow: "0 16px 36px rgba(15, 23, 42, 0.08)",
            overflow: "hidden"
          }}
        >
            {loading ? (
              <div className="d-flex justify-content-center p-5">
                <CircularProgress />
              </div>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Name</strong></TableCell>
                      <TableCell><strong>Email</strong></TableCell>
                      <TableCell><strong>Address</strong></TableCell>
                      <TableCell align="center"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customers.length > 0 ? (
                      customers.map((row) => (
                        <TableRow key={row._id}>
                          <TableCell>{row.name}</TableCell>
                          <TableCell>{row.email}</TableCell>
                          <TableCell>{row.address}</TableCell>
                          <TableCell align="center" >


                            <IconButton
                              sx={{ backgroundColor: "#fff", boxShadow: "0 1px 5px rgba(15,23,42,0.12)", mr: 1, "&:hover": { backgroundColor: "#F8FAFC" } }}
                              color="primary" 
                              onClick={() => handleOpenModal(row)}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton 
                              sx={{ backgroundColor: "#fff", boxShadow: "0 1px 5px rgba(15,23,42,0.12)", "&:hover": { backgroundColor: "#FFF5F5" } }}
                              color="error" 
                              onClick={() => setDeleteId(row._id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center" className="py-4">
                          No flagged customers found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
        </Paper>
      </Box>

      {/* Add/Edit Modal */}
      <Dialog open={modalOpen} onClose={handleCloseModal} fullWidth maxWidth="sm">
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {isEdit ? "Edit Customer" : "Add Customer Flag"}
          </DialogTitle>
          <DialogContent dividers>
            <div className="py-2">
              <TextField
                label="Name"
                variant="outlined"
                fullWidth
                required
                className="mb-3"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
              />
              <TextField
                label="Email"
                type="email"
                variant="outlined"
                fullWidth
                required
                className="mb-3"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
              />
              <TextField
                label="Address"
                variant="outlined"
                fullWidth
                required
                multiline
                rows={2}
                value={formData.address}
                onChange={(e) => handleChange("address", e.target.value)}
              />
            </div>
          </DialogContent>
          <DialogActions className="p-3">
            <Button onClick={handleCloseModal} color="secondary" variant="outlined">
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="btn_bg_secondery text-white" 
              variant="contained"
              disabled={submitLoading}
            >
              {submitLoading ? <CircularProgress size={24} color="inherit" /> : "Save"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} fullWidth maxWidth="xs">
        <DialogTitle>Remove flagged customer?</DialogTitle>
        <DialogContent dividers>
          This customer will be removed from the flagged list.
        </DialogContent>
        <DialogActions className="p-3">
          <Button onClick={() => setDeleteId(null)} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CustomerFlagged
